/**
 * Codex (OpenAI) config writer
 *
 * Skill: ~/.agents/skills/sailfish-autofix/ (cross-client) + ~/.codex/skills/
 * MCP: ~/.codex/config.toml [mcp_servers.sailfishAutofix]
 */

import { homedir } from "os";
import { join } from "path";
import { existsSync, readFileSync, writeFileSync, mkdirSync } from "fs";
import type { AgentConfigWriter } from "./base.js";
import { copySkillTo, removeSkillFrom, installCrossClientSkill, removeCrossClientSkill, buildMcpEntry } from "./base.js";
import { getCredentialsPath } from "../auth/credentials.js";

const CODEX_SKILLS_DIR = join(homedir(), ".codex", "skills");
const CODEX_CONFIG = join(homedir(), ".codex", "config.toml");

const TOML_BEGIN_MARKER = "# BEGIN SAILFISH AUTOFIX MANAGED";
const TOML_END_MARKER = "# END SAILFISH AUTOFIX MANAGED";

/**
 * Append MCP server config to Codex TOML using markers for clean removal.
 */
function addTomlMcpSection(content: string): string {
  // Remove existing section if present
  content = removeTomlMcpSection(content);

  const entry = buildMcpEntry();
  const argsToml = "[" + entry.args.map((a) => `"${a}"`).join(", ") + "]";
  const envLines = Object.entries(entry.env)
    .map(([k, v]) => `  ${k} = "${v}"`)
    .join("\n");

  const section = [
    TOML_BEGIN_MARKER,
    `[mcp_servers.sailfishAutofix]`,
    `command = "${entry.command}"`,
    `args = ${argsToml}`,
    ``,
    `[mcp_servers.sailfishAutofix.env]`,
    envLines,
    TOML_END_MARKER,
  ].join("\n");

  return content.trimEnd() + "\n\n" + section + "\n";
}

function removeTomlMcpSection(content: string): string {
  const beginIdx = content.indexOf(TOML_BEGIN_MARKER);
  if (beginIdx === -1) return content;
  const endIdx = content.indexOf(TOML_END_MARKER);
  if (endIdx === -1) return content;

  const before = content.slice(0, beginIdx).replace(/\n+$/, "");
  const after = content.slice(endIdx + TOML_END_MARKER.length).replace(/^\n+/, "");

  if (!before && !after) return "";
  if (!before) return after;
  if (!after) return before + "\n";
  return before + "\n\n" + after;
}

export const codex: AgentConfigWriter = {
  id: "codex",
  name: "Codex (OpenAI)",

  installSkill(global: boolean) {
    installCrossClientSkill(global);
    copySkillTo(CODEX_SKILLS_DIR);
  },

  installMcp() {
    let content = "";
    if (existsSync(CODEX_CONFIG)) {
      content = readFileSync(CODEX_CONFIG, "utf-8");
    }
    content = addTomlMcpSection(content);
    mkdirSync(join(homedir(), ".codex"), { recursive: true });
    writeFileSync(CODEX_CONFIG, content);
  },

  uninstallSkill() {
    removeCrossClientSkill(true);
    removeSkillFrom(CODEX_SKILLS_DIR);
  },

  uninstallMcp() {
    if (!existsSync(CODEX_CONFIG)) return;
    let content = readFileSync(CODEX_CONFIG, "utf-8");
    content = removeTomlMcpSection(content);
    writeFileSync(CODEX_CONFIG, content);
  },
};
